import Component from 'flarum/common/Component';
import avatar from 'flarum/common/helpers/avatar';
import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import Stream from 'flarum/common/utils/Stream';
import classList from 'flarum/common/utils/classList';

export default class CustomAvatarEditor extends Component {
  oninit(vnode) {
    super.oninit(vnode);

    this.user = this.attrs.user;
    this.loading = false;
    this.editingNickname = false;
    this.nickname = Stream(this.user.displayName() || '');
    this.nicknameError = null;
    this.showSuccessIcon = false;

    this.nickNameCanChanged = this.getPermissionFromStorage('nick_name_can_changed', true);
    this.headimgCanChanged = this.getPermissionFromStorage('headimg_can_changed', true);
  }

  /**
   * 使用 Flarum 原生顶部 Alert，与系统保存设置/发帖成功提示一致
   */
  showAlert(type, message, delay = 4000) {
    const alertId = app.alerts.show({ type }, message);
    if (delay > 0) {
      setTimeout(() => app.alerts.dismiss(alertId), delay);
    }
    return alertId;
  }

  getPermissionFromStorage(key, defaultValue = true) {
    try {
      const value = localStorage.getItem(key);
      if (value !== null) {
        return JSON.parse(value);
      }
    } catch (e) {
      console.warn(`无法读取 ${key} 权限信息:`, e);
    }
    return defaultValue;
  }

  view() {
    const auditHint = app.translator.trans('tf-custom-profile.forum.profile.audit_hint');

    return (
      <div className={classList('CustomAvatarEditor', this.attrs.className)}>
        <div className="CustomAvatarEditor-avatar">
          {avatar(this.user, { className: 'CustomAvatarEditor-avatar-img' })}
          {this.headimgCanChanged && (
            <label
              className="CustomAvatarEditor-upload-btn"
              aria-label={auditHint}
            >
              <input
                type="file"
                accept="image/*"
                style="display: none;"
                onchange={(e) => this.uploadAvatar(e.target.files[0])}
                disabled={this.loading}
              />
              <i className="fa-light fa-camera" />
              <span className="CustomAvatarEditor-audit-tooltip">{auditHint}</span>
            </label>
          )}
        </div>

        {this.editingNickname ? (
          <div className="CustomAvatarEditor-nickname-edit">
            <input
              type="text"
              className="FormControl CustomAvatarEditor-nickname-input"
              value={this.nickname()}
              oninput={(e) => {
                this.nickname(e.target.value);
                this.nicknameError = null;
              }}
              disabled={this.loading}
              placeholder={app.translator.trans('tf-custom-profile.forum.profile.nickname_placeholder')}
            />
            {this.nicknameError && (
              <div className="CustomAvatarEditor-error">{this.nicknameError}</div>
            )}
            <div className="CustomAvatarEditor-nickname-actions">
              <Button
                className="Button Button--primary"
                onclick={() => this.saveNickname()}
                loading={this.loading}
              >
                {app.translator.trans('tf-custom-profile.forum.profile.save')}
              </Button>
              <Button
                className="Button"
                onclick={() => this.cancelEditNickname()}
                disabled={this.loading}
              >
                {app.translator.trans('tf-custom-profile.forum.profile.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="CustomAvatarEditor-nickname-display">
            <span className="CustomAvatarEditor-nickname-text">{this.user.displayName()}</span>
            {this.showSuccessIcon && (
              <i className="fa-light fa-circle-check CustomAvatarEditor-success-icon" />
            )}
            {this.nickNameCanChanged && (
              <button
                type="button"
                className="CustomAvatarEditor-edit-btn"
                onclick={() => this.startEditNickname()}
                disabled={this.loading}
                aria-label={auditHint}
              >
                <i className="fa-light fa-pen-to-square" aria-hidden="true" />
                <span className="CustomAvatarEditor-audit-tooltip">{auditHint}</span>
              </button>
            )}
          </div>
        )}

      </div>
    );
  }

  uploadAvatar(file) {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.showAlert('error', app.translator.trans('tf-custom-profile.forum.profile.avatar_invalid_type'));
      return;
    }

    this.loading = true;
    m.redraw();

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Data = e.target.result;

      app
        .request({
          method: 'POST',
          url: app.forum.attribute('apiUrl') + '/sso/modify-avatar',
          body: {
            avatar_base64: base64Data,
          },
        })
        .then((response) => {
          if (response && response.data) {
            this.user.pushAttributes(response.data.attributes);
            app.session.user = this.user;
          }

          this.showAlert('success', app.translator.trans('tf-custom-profile.forum.profile.avatar_updated'));
          this.loading = false;
          m.redraw();
        })
        .catch((error) => {
          console.error('Avatar upload error:', error);
          this.showAlert(
            'error',
            error.response?.errors?.[0]?.detail ||
              app.translator.trans('tf-custom-profile.forum.profile.avatar_upload_failed')
          );
          this.loading = false;
          m.redraw();
        });
    };

    reader.onerror = () => {
      this.showAlert('error', app.translator.trans('tf-custom-profile.forum.profile.avatar_read_failed'));
      this.loading = false;
      m.redraw();
    };

    reader.readAsDataURL(file);
  }

  startEditNickname() {
    this.editingNickname = true;
    this.nickname(this.user.displayName() || '');
    this.nicknameError = null;
    m.redraw();
  }

  cancelEditNickname() {
    this.editingNickname = false;
    this.nickname(this.user.displayName() || '');
    this.nicknameError = null;
    m.redraw();
  }

  saveNickname() {
    const newNickname = this.nickname().trim();

    if (!newNickname) {
      this.nicknameError = app.translator.trans('tf-custom-profile.forum.profile.nickname_required');
      m.redraw();
      return;
    }

    if (newNickname === this.user.displayName()) {
      this.cancelEditNickname();
      return;
    }

    this.loading = true;
    this.nicknameError = null;
    m.redraw();

    app
      .request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/sso/modify-nickname',
        body: {
          nickname: newNickname,
        },
      })
      .then((response) => {
        if (response && response.data) {
          this.user.pushAttributes(response.data.attributes);
          app.session.user = this.user;
        }

        this.showAlert('success', app.translator.trans('tf-custom-profile.forum.profile.nickname_updated'));
        this.showSuccessIcon = true;
        this.editingNickname = false;
        this.loading = false;
        m.redraw();

        setTimeout(() => {
          this.showSuccessIcon = false;
          m.redraw();
        }, 3000);
      })
      .catch((error) => {
        console.error('Nickname update error:', error);
        this.nicknameError =
          error.response?.errors?.[0]?.detail ||
          app.translator.trans('tf-custom-profile.forum.profile.nickname_update_failed');
        this.loading = false;
        m.redraw();
      });
  }
}
